import * as React from 'react';
import { SimpleTooltip } from '../../atoms/tooltip/prebuilt/simple-tooltip/simple-tooltip';
import type { ISideNavItemProps } from './types';
import { cn } from '@utils';

const TOOLTIP_DELAY_MS = 300;
const COLLAPSED_ITEM_TOOLTIP_SIDE = 'right';

interface IGetSideNavItemClassNameParams {
	collapsed: boolean;
	comfortable: boolean;
	active: boolean;
	disabled: boolean;
	className?: string;
}

function getSideNavItemClassName({
	collapsed,
	comfortable,
	active,
	disabled,
	className,
}: IGetSideNavItemClassNameParams): string {
	return cn(
		'font-sans flex items-center w-full bg-transparent border-0 cursor-pointer text-left !no-underline rounded-[0.5rem] font-normal transition-colors duration-200',
		'[-webkit-tap-highlight-color:transparent]',
		collapsed
			? 'justify-center p-[1rem]'
			: comfortable
				? 'gap-[1.4rem] px-[1.6rem] py-[1.4rem]'
				: 'gap-[1.2rem] px-[1.2rem] py-[1rem]',
		active
			? 'bg-white/95 !text-primary shadow-[0_2px_8px_rgba(0,0,0,0.15)]'
			: [
					'!text-white/80 focus:!text-white/80 focus-visible:!text-white active:!text-white/80',
					'[@media(hover:hover)_and_(pointer:fine)]:hover:bg-white/10',
					'[@media(hover:hover)_and_(pointer:fine)]:hover:!text-white',
				].join(' '),
		disabled && 'opacity-50 pointer-events-none cursor-not-allowed',
		className
	);
}

/**
 * Presentational nav row with optional collapsed-state tooltip.
 */
export const SideNavItem: React.FC<ISideNavItemProps> = ({
	icon,
	label,
	active = false,
	disabled = false,
	collapsed = false,
	comfortable = false,
	href,
	target,
	rel,
	onClick,
	className,
	'data-testid': dataTestId,
	'aria-label': ariaLabel,
}) => {
	const isLink = href !== undefined && !disabled;
	const itemClassName = getSideNavItemClassName({ collapsed, comfortable, active, disabled, className });
	const content = (
		<>
			<span
				className={cn(
					'inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full',
					comfortable ? 'w-[2.4rem] h-[2.4rem]' : 'w-[2rem] h-[2rem]',
					active ? 'text-primary' : 'text-current'
				)}
				aria-hidden="true"
			>
				{icon}
			</span>
			{!collapsed && (
				<span
					className={cn(
						'menu-text truncate leading-[130%] font-normal',
						comfortable ? 'text-[1.6rem]' : 'text-[1.4rem]',
						active && 'font-semibold'
					)}
				>
					{label}
				</span>
			)}
		</>
	);

	const control = isLink ? (
		<a
			href={href}
			target={target}
			rel={rel}
			onClick={onClick}
			aria-current={active ? 'page' : undefined}
			aria-label={ariaLabel ?? label}
			data-testid={dataTestId}
			className={itemClassName}
		>
			{content}
		</a>
	) : (
		<button
			type="button"
			disabled={disabled}
			onClick={onClick}
			aria-current={active ? 'page' : undefined}
			aria-label={ariaLabel ?? label}
			data-testid={dataTestId}
			className={itemClassName}
		>
			{content}
		</button>
	);

	if (!collapsed) {
		// eslint-disable-next-line jsx-a11y/no-redundant-roles -- parent list-none strips implicit listitem semantics in Safari
		return <li role="listitem">{control}</li>;
	}

	return (
		// eslint-disable-next-line jsx-a11y/no-redundant-roles -- parent list-none strips implicit listitem semantics in Safari
		<li role="listitem">
			<SimpleTooltip
				content={label}
				asChild
				delayDuration={TOOLTIP_DELAY_MS}
				disabled={disabled}
				side={COLLAPSED_ITEM_TOOLTIP_SIDE}
				avoidCollisions={false}
			>
				{control}
			</SimpleTooltip>
		</li>
	);
};

SideNavItem.displayName = 'SideNavItem';
